import V2Module from "~/repository/modules/v2";
import {$fetch} from 'ofetch';
import type {FetchContext, FetchOptions, FetchResponse} from 'ofetch';
import type {IApi} from "~/types/IApi";

export default defineNuxtPlugin((nuxtApp) => {
  const tokenCookie = useCookie('wad-auth._token.local');
  const { token } = useAuth();
  // Resolved here, during plugin setup, so the interceptors below can be called later from
  // outside a Nuxt context.
  const { markOnline, verifyConnectivity } = useConnectivity();
  const signingOut = useSignOutState();

  // One forced teardown per page lifetime: a dead token fails every authenticated call on the page
  // at once, and each of them would otherwise start its own.
  let unauthorizedTeardownStarted = false;

  const requestCarriedBearerToken = (options: FetchOptions): boolean => {
    const headers = options.headers as Headers | Record<string, string> | undefined;
    if (!headers) return false;
    const value = headers instanceof Headers
      ? headers.get('authorization')
      : (headers.Authorization ?? headers.authorization);
    return !!value;
  };

  // A 401 on a call that carried a bearer token means the server no longer honours this session:
  // the token was revoked by a password change or a sign-out elsewhere, or the account was
  // deactivated. Nothing in the app recovers from that on its own - nuxt-auth only notices on its
  // next session refresh, and every personal call until then fails quietly, which the attendee
  // sees as an empty agenda and missing workshop seats - so end the session here and send them
  // to log in again.
  const onUnauthorizedResponse = (context: FetchContext & { response: FetchResponse<unknown> }) => {
    if (context.response.status !== 401) return;
    // Without a token this is a guest reaching a member-only endpoint, not a dead session. Both
    // checks are needed: the fetcher keeps the boot-time cookie as a default header, so after a
    // deliberate sign-out a public call can still carry the old token while the app is a guest.
    if (!token.value || !requestCarriedBearerToken(context.options)) return;
    // The user menu's sign-out is already tearing the session down and lands on the login page.
    if (signingOut.value || unauthorizedTeardownStarted) return;
    unauthorizedTeardownStarted = true;
    void nuxtApp.runWithContext(() =>
      useSessionTeardown().tearDownSession({ redirectTo: '/user/login?session_expired=1' })
    );
  };

  const fetchOptions: FetchOptions = {
    baseURL: nuxtApp.$config.public.apiBase,
    // 'include', not 'same-origin': the API is on another subdomain, and the Cloudflare
    // challenge clearance (cf_clearance, zone-wide cookie) must ride along on API calls —
    // otherwise a user who passed the managed challenge still gets every XHR re-challenged
    // (unsolvable in XHR, surfaces as a CORS/network error).
    credentials: 'include',
    // Without this, a slow/flaky connection lets a request hang indefinitely instead of
    // failing — which defeats every caller's error handling (including the offline-vs-500
    // classification in createDataLoadError, which relies on the request actually settling).
    timeout: 20_000,
    headers: {
      "Content-Type": 'application/json; charset=UTF-8'
    },
    // Connectivity is derived from real traffic rather than from navigator.onLine, which has
    // been observed stuck false while every request succeeded (see useConnectivity).
    //
    // Any response at all proves the connection works, including a 4xx or 5xx. A request that
    // fails without one is only a suspicion: it is also what an abort or a single flaky
    // request looks like, so it asks for a probe instead of flipping the app into offline mode.
    onResponse: () => markOnline(),
    onRequestError: () => {
      verifyConnectivity();
    },
  };

  const createApiFetcher = (withAuth: boolean) => {
    const options = {...fetchOptions};
    if (withAuth) {
      options.onResponseError = onUnauthorizedResponse;
    }
    if (withAuth && tokenCookie.value) {
      options.headers = {
        Authorization: `Bearer ${tokenCookie.value}`,
      };
    }

    return $fetch.create(options);
  };

  let apiFetcher = createApiFetcher(true);
  let apiFetcherNoAuth = createApiFetcher(false);

  watch(tokenCookie, (_) => {
    apiFetcher = createApiFetcher(true);
  });

  const modules: IApi = {
    v2: new V2Module(apiFetcher, apiFetcherNoAuth, token),
  };

  return {
    provide: {
      api: modules,
    },
  };
});
